import React, {useEffect, useState} from 'react'
import styled from 'styled-components'
import {useNavigation} from 'react-navi'
import {Helmet} from 'react-navi-helmet-async'
import {useTranslation} from 'react-i18next'
import {v4 as uuid} from 'uuid'

import {MainLayoutWithoutStretch, MainContent} from '../../../Layout/Layout'
import {GoBackHeaderTitle, HeaderRow} from '../../../Molecules/ViewComponents/ViewHeader'
import {useAppState} from '../../../../state'
import {Button, ButtonRowGrid, ButtonRowWrap} from '../../../Atoms/Buttons'
import {BigLabelInput, Label, Select as NativeSelect} from '../../../Atoms/Forms'
import {P} from '../../../Atoms/Typography'
import {Chain, NotificationType} from '../../../../state/state'
import {AssetData, DocumentFolder, DocumentType} from '../../../../state/rest'
import {IconDocument} from '../../../../Assets/Icons/TinyIcons'
import {Loader} from '../../../../Routes'
import SiteSelectionCheckboxStyle from '../../../../Components/Organisms/SiteSelectionCheckboxStyle'

type UploadEntry = {
  id: string
  asset?: AssetData
  editedName: string
  folderId: string
  error?: string
}

const PageContainer = styled.div`
  width: min(960px, calc(100% - 4rem));
  margin: 0 auto;
  padding: 0 0 4rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-row-gap: 1.25rem;
`

const IntroRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.25rem;
`

const FilesSection = styled.section`
  display: grid;
  grid-row-gap: 1rem;
`

const FileLayout = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem;
  align-items: center;
`

const FileFieldsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.95fr);
  gap: 1rem;
  align-items: end;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`

const FileList = styled.div`
  display: grid;
  grid-row-gap: 1rem;
`

const FileCard = styled.div`
  border: 1px solid #e0e5f2;
  border-radius: 16px;
  background: #ffffff;
  padding: 1.25rem;
`

const FileIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #e8edff;
`

const FileMeta = styled.div`
  display: grid;
  grid-row-gap: 0.75rem;
`

const InlineError = styled(P)`
  color: #c62828;
  margin: 0;
`

const EmptyState = styled.div`
  border: 1px dashed #cbd4ea;
  border-radius: 16px;
  background: #f9fbff;
  padding: 2rem 1.25rem;
  text-align: center;
`

const AddFilesView = () => {
  const nav = useNavigation()
  const {state, actions} = useAppState()
  const {t} = useTranslation('documents')
  const [entries, setEntries] = useState<UploadEntry[]>([])
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([])
  const [isChainWide, setIsChainWide] = useState(false)
  const [pageError, setPageError] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)

  const me = state.me!
  const chain = (state.chainsById as any)[state.selectedChainId!] as Chain
  const internalDocumentFolders = (state.v1.documents.internalDocumentFolders as unknown) as DocumentFolder[]
  const isLoading = state.v1.documents.documentFoldersPending
  let writableIds = me.accessRights.write.sites
  writableIds = writableIds.concat(...me.accessRights.write.chains)

  const folderOptions = internalDocumentFolders.map((folder: DocumentFolder) => ({id: folder.id, option: folder.name}))

  useEffect(() => {
    actions.v1.documents.getDocumentFolders()
  }, [actions.v1.documents])

  useEffect(() => {
    const pendingAssets = state.v1.documents.pendingUploadedAssets || []

    if (entries.length === 0 && pendingAssets.length > 0) {
      setEntries(
        pendingAssets.map(asset => ({
          id: uuid(),
          asset,
          editedName: asset.name,
          folderId: ''
        }))
      )
      actions.v1.documents.clearPendingUploadedAssets()
      return
    }

    if (!isLoading && pendingAssets.length === 0 && entries.length === 0) {
      nav.navigate('/documents')
    }
  }, [actions.v1.documents, entries.length, isLoading, nav, state.v1.documents.pendingUploadedAssets])

  useEffect(() => {
    if (!isLoading && internalDocumentFolders.length === 0) {
      actions.addNotification({
        id: uuid(),
        type: NotificationType.ERROR,
        title: t('documents:msg.fileRequiresFolder', 'File cannot be added without a folder.'),
        visible: true,
        hideAfterDelay: 6000
      })
      nav.navigate('/documents')
    }
  }, [actions, internalDocumentFolders.length, isLoading, nav, t])

  const notifyError = (title: string, description?: string) => {
    actions.addNotification({
      id: uuid(),
      type: NotificationType.ERROR,
      title,
      description,
      visible: true,
      hideAfterDelay: 6000
    })
  }

  const notifyInfo = (title: string, description?: string) => {
    actions.addNotification({
      id: uuid(),
      type: NotificationType.INFO,
      title,
      description,
      visible: true,
      hideAfterDelay: 5000
    })
  }

  const hasInvalidUploadedEntries = entries.some(
    entry => entry.editedName.trim().length === 0 || entry.folderId.trim().length === 0
  )
  const isSaveDisabled =
    isSaving || entries.length === 0 || hasInvalidUploadedEntries || (!isChainWide && selectedSiteIds.length === 0)

  const handleSave = async () => {
    if (entries.length === 0) {
      setPageError(t('documents:msg.uploadAtLeastOneFile', 'Upload at least one file before saving.'))
      return
    }
    if (entries.some(entry => entry.editedName.trim().length === 0)) {
      setPageError(t('documents:msg.fileNameRequired', 'File name cannot be empty.'))
      return
    }
    if (entries.some(entry => entry.folderId.trim().length === 0)) {
      setPageError(t('documents:msg.folderRequired', 'Folder selection is required for every file.'))
      return
    }
    if (!isChainWide && selectedSiteIds.length === 0) {
      setPageError(t('documents:msg.siteSelectionRequired', 'Select at least one site.'))
      return
    }

    setPageError('')
    setIsSaving(true)

    try {
      const createdIds: string[] = []
      const savedEntryIds = new Set<string>()
      const failedEntries: Record<string, string> = {}

      for (const entry of entries) {
        try {
          if (!entry.asset?.id) {
            throw new Error(t('documents:msg.uploadFailed', 'Upload failed.'))
          }

          const created = await actions.v1.documents.createDocument({
            type: DocumentType.Internal,
            name: entry.editedName.trim(),
            asset: entry.asset,
            documentFolder: {id: entry.folderId},
            accessType: isChainWide ? 'ChainWide' : 'SiteSpecific',
            sites: selectedSiteIds.map(siteId => ({documentId: '', siteId}))
          } as any)

          const createdId = (created as any)?.id || (created as any)?.data?.id

          if (!createdId) {
            throw new Error(t('documents:msg.createDocumentError', 'Could not create document'))
          }

          createdIds.push(createdId)
          savedEntryIds.add(entry.id)
        } catch (error) {
          failedEntries[entry.id] =
            error instanceof Error ? error.message : t('documents:msg.createDocumentError', 'Could not create document')
        }
      }

      setEntries(current =>
        current
          .filter(entry => !savedEntryIds.has(entry.id))
          .map(entry =>
            failedEntries[entry.id]
              ? {
                  ...entry,
                  error: failedEntries[entry.id]
                }
              : entry
          )
      )

      if (Object.keys(failedEntries).length > 0) {
        notifyError(
          t('documents:msg.partialSaveError', 'Some files could not be saved.'),
          t('documents:msg.partialSaveErrorDescription', 'Review the remaining files and try again.')
        )
        return
      }

      notifyInfo(t('documents:msg.filesSaved', 'Files saved successfully.'))
      nav.navigate(createdIds[0] ? `/documents?open=${createdIds[0]}` : '/documents')
    } catch (error) {
      notifyError(
        t('documents:msg.createDocumentError', 'Could not create document'),
        error instanceof Error ? error.message : undefined
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <MainLayoutWithoutStretch>
      <Helmet title={t('common:routes.createDocument', 'Create document')} />
      <Loader show={isLoading} />
      {!isLoading && (
        <MainContent variant="white">
          <HeaderRow>
            <GoBackHeaderTitle
              label={t('documents:labels.addNewFile', 'Add new file')}
              backLabel={t('documents:labels.documentsTitle', 'Documents')}
              onBack={() => nav.navigate('/documents')}
            />
          </HeaderRow>
          <PageContainer>
            <IntroRow>
              <Label>{t('documents:labels.addDocument', 'Add document')}</Label>
            </IntroRow>
            <FilesSection>
              {entries.length === 0 ? (
                <EmptyState>
                  <P>{t('documents:messages.noUploadedFiles', 'Uploaded files will appear here.')}</P>
                </EmptyState>
              ) : (
                <FileList>
                  {entries.map(entry => (
                    <FileCard key={entry.id}>
                      <FileLayout>
                        <FileIconWrap>
                          <IconDocument />
                        </FileIconWrap>
                        <FileMeta>
                          <FileFieldsRow>
                            <BigLabelInput
                              labelText={t('documents:labels.documentName', 'Document name')}
                              id={`uploaded-name-${entry.id}`}
                              value={entry.editedName}
                              errorMsg={
                                entry.editedName.trim().length === 0
                                  ? t('documents:msg.fileNameRequired', 'File name cannot be empty.')
                                  : undefined
                              }
                              onChange={event =>
                                setEntries(current =>
                                  current.map(item =>
                                    item.id === entry.id
                                      ? {...item, editedName: event.target.value, error: undefined}
                                      : item
                                  )
                                )
                              }
                            />
                            <NativeSelect
                              label={t('documents:labels.documentFolder', 'Document folder')}
                              id={`folder-${entry.id}`}
                              options={folderOptions}
                              emptyStr={t('documents:placeholders.selectFolder', 'Select folder')}
                              returnId
                              required
                              nativeProps={{
                                value: entry.folderId,
                                onChange: event =>
                                  setEntries(current =>
                                    current.map(item =>
                                      item.id === entry.id
                                        ? {...item, folderId: event.target.value, error: undefined}
                                        : item
                                    )
                                  )
                              }}
                            />
                          </FileFieldsRow>
                          {entry.error ? <InlineError>{entry.error}</InlineError> : null}
                        </FileMeta>
                      </FileLayout>
                    </FileCard>
                  ))}
                </FileList>
              )}
            </FilesSection>

            {pageError ? <InlineError>{pageError}</InlineError> : null}

            <ButtonRowWrap>
              <div />
              <ButtonRowGrid>
                <Button variant="secondary" negative onClick={() => nav.navigate('/documents')}>
                  {t('common:buttons.cancel', 'Cancel')}
                </Button>
                <Button variant="secondary" disabled={isSaveDisabled} onClick={handleSave}>
                  {isSaving ? t('common:buttons.saving', 'Saving...') : t('common:buttons.save', 'Save')}
                </Button>
              </ButtonRowGrid>
            </ButtonRowWrap>
          </PageContainer>
        </MainContent>
      )}
      <SiteSelectionCheckboxStyle
        infoText={t('documents:labels.selectSitesVisibility', 'Select sites to which these files are visible')}
        writableIds={writableIds}
        chain={chain as any}
        isChainWide={isChainWide}
        setIsChainWide={setIsChainWide}
        selectedSites={selectedSiteIds}
        setSelectedSites={setSelectedSiteIds}
        isLoading={isLoading}
        t={t}
      />
    </MainLayoutWithoutStretch>
  )
}

export default AddFilesView
